/**
 * Looping sound for a persistent order alert (see orderAlert.js). Plays the tenant's uploaded alert sound on
 * repeat; if there is none, or it fails to load or start within a few seconds, it repeats the built-in urgent
 * tone instead, so an alert is never silent. Stops when ``stop()`` is called or after ``maxMs``.
 *
 * In-app audio plays on the phone's media volume and only while the app is open; the OS notification sound for
 * background and closed apps is separate and unchanged.
 */
import { isMuted, playNotifSound } from "./notifSound";

export const ALERT_SOUND_MAX_MS = 60000;
export const SYNTH_REPEAT_MS = 2200;
export const AUDIO_START_TIMEOUT_MS = 4000;
const VIBRATE_PATTERN = [400, 200, 400];

const defaults = {
    createAudio: (url) => new Audio(url),
    synth: () => playNotifSound({ urgent: true, gain: 0.9, wave: "triangle" }),
    vibrate: (p) => { try { navigator.vibrate?.(p); } catch { /* iOS WebKit has no vibration */ } },
    muted: isMuted,
    setTimeout: (fn, ms) => setTimeout(fn, ms),
    clearTimeout: (id) => clearTimeout(id),
    setInterval: (fn, ms) => setInterval(fn, ms),
    clearInterval: (id) => clearInterval(id),
};

/**
 * Start the loop. Returns an idempotent ``stop()``.
 *
 * ``repeat: false`` plays the alert once (and vibrates once) instead of looping until acknowledged — the tenant's
 * per-event choice, see backend/notification_policy.py. The dialog still stays until the user acts on it.
 */
export function startAlertLoop({ url = null, maxMs = ALERT_SOUND_MAX_MS, repeat = true, deps = {} } = {}) {
    const d = { ...defaults, ...deps };
    let stopped = false;
    let audio = null;
    let synthTimer = null;
    let startTimer = null;
    let usingSynth = false;

    const vibrateTimer = repeat ? d.setInterval(() => d.vibrate(VIBRATE_PATTERN), SYNTH_REPEAT_MS) : null;
    d.vibrate(VIBRATE_PATTERN);

    const releaseAudio = () => {
        if (!audio) return;
        try { audio.pause(); } catch { /* ignore */ }
        try { audio.removeAttribute?.("src"); audio.load?.(); } catch { /* ignore */ }
        audio = null;
    };

    const fallbackToSynth = () => {
        if (stopped || usingSynth) return;
        usingSynth = true;
        if (startTimer != null) d.clearTimeout(startTimer);
        releaseAudio();
        d.synth();
        if (repeat) synthTimer = d.setInterval(() => { if (!stopped) d.synth(); }, SYNTH_REPEAT_MS);
    };

    if (!d.muted()) {
        if (url) {
            try {
                audio = d.createAudio(url);
                audio.loop = repeat;
                audio.volume = 1;
                audio.addEventListener?.("error", fallbackToSynth);
                audio.addEventListener?.("playing", () => { if (startTimer != null) d.clearTimeout(startTimer); });
                // A URL that hangs never fires "error"; don't wait on it forever.
                startTimer = d.setTimeout(fallbackToSynth, AUDIO_START_TIMEOUT_MS);
                const p = audio.play();
                if (p && typeof p.catch === "function") p.catch(fallbackToSynth);
            } catch {
                fallbackToSynth();
            }
        } else {
            fallbackToSynth();
        }
    }

    const maxTimer = d.setTimeout(() => stop(), maxMs);

    function stop() {
        if (stopped) return;
        stopped = true;
        d.clearTimeout(maxTimer);
        if (startTimer != null) d.clearTimeout(startTimer);
        if (synthTimer != null) d.clearInterval(synthTimer);
        if (vibrateTimer != null) d.clearInterval(vibrateTimer);
        releaseAudio();
        d.vibrate(0);
    }
    return stop;
}
